import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Multiselect from 'multiselect-react-dropdown';
import {
  addRequirement,
  updateLiveTask,
  addMachineryRequirement,
  getMachineryList,
  deleteDailyTask,
  getAllBuilderCategories,
  getAllItemsByMulCategoryID,
  getMyData
} from '../../../services/api/implementation/impl';
import Button from '../../../../shared/components/ui/Button';
import Modal from '../../../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import { ChevronDown, Package, Wrench, Users } from 'lucide-react';
import { AddMaterials } from '../../../components/forms/AddMaterials';

const AddRequirementAll = ({ selectedTask, projectId, onSuccess, isOpen, onClose }) => {
  // State management
  const [requirementStep, setRequirementStep] = useState('items');
  const [loading, setLoading] = useState(false);
  const [trackSkip, setTrackSkip] = useState({
    items: false,
    machinery: false,
    labour: false
  });

  // Items state
  const [selectedItems, setSelectedItems] = useState([]);
  const [quantities, setQuantities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryChips, setCategoryChips] = useState([]);
  const [items, setItems] = useState([]);
  const [builderId, setBuilderId] = useState();
  const [OpenAddMaterial, setOpenAddMaterial] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  // Machinery state
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [machineList, setMachineList] = useState([]);

  // Labour state
  const [skilledLabour, setSkilledLabour] = useState('');
  const [unskilledLabour, setUnskilledLabour] = useState('');

  // Fetch my data to get builder ID
  const { data: myData, refetch: refetchMyData } = useQuery({
    queryKey: ['myData'],
    queryFn: getMyData
  });

  // Fetch categories
  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    refetch: refetchCategories
  } = useQuery({
    queryKey: ['categories', builderId],
    queryFn: () => getAllBuilderCategories(builderId),
    enabled: !!builderId
  });

  // Fetch machinery list
  const { data: machineryData, isLoading: machineryLoading } = useQuery({
    queryKey: ['machinery'],
    queryFn: () => getMachineryList()
  });

  // Refetch myData on mount
  useEffect(() => {
    refetchMyData();
  }, []);

  // Set builder ID from myData
  useEffect(() => {
    if (myData) {
      const id = myData?.parent_user_id;
      if (id) {
        setBuilderId(id);
      }
    }
  }, [myData]);

  // Update state when data is fetched
  useEffect(() => {
    if (categoriesData) {
      const transformedData = categoriesData?.data?.map((item) => ({
        id: item.id,
        name: item.name,
        value: item.id
      }));
      setCategories(transformedData);
    }
  }, [categoriesData]);

  useEffect(() => {
    if (machineryData) {
      setMachineList(machineryData?.data);
    }
  }, [machineryData]);

  // Fetch items when categories change
  const fetchItemsByCategories = async () => {
    if (categoryChips.length > 0) {
      try {
        const categoryIds = categoryChips.map((chip) => chip.id).join(',');
        const res = await getAllItemsByMulCategoryID(categoryIds);
        setItems(res || []);
      } catch (err) {
        console.error('Error fetching items:', err);
        toast.error(err?.response?.data?.message || err?.message || 'Error fetching items');
        setItems([]);
      }
    } else {
      setItems([]);
    }
  };
  useEffect(() => {
    fetchItemsByCategories();
  }, [categoryChips]);

  // Reset all data
  const resetAllData = () => {
    setSelectedItems([]);
    setQuantities([]);
    setCategoryChips([]);
    setItems([]);
    setSkilledLabour('');
    setUnskilledLabour('');
    setSelectedMachines([]);
    setRequirementStep('items');
    setTrackSkip({ items: false, machinery: false, labour: false });
    setLoading(false);
  };

  // Handle modal close
  const handleClose = () => {
    if (requirementStep === 'items') {
      onClose();
      resetAllData();
    } else if (requirementStep === 'machinery') {
      setRequirementStep('items');
    } else if (requirementStep === 'labour') {
      setRequirementStep('machinery');
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (requirementStep === 'machinery') {
      setRequirementStep('items');
    } else if (requirementStep === 'labour') {
      setRequirementStep('machinery');
    }
  };

  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    setSelectedItems((prevSelectedItems) => {
      if (prevSelectedItems.includes(itemId)) {
        const updatedSelectedItems = prevSelectedItems.filter((i) => i !== itemId);
        setQuantities((prevQuantities) => prevQuantities.filter((q) => q.id !== itemId));
        return updatedSelectedItems;
      } else {
        return [...prevSelectedItems, itemId];
      }
    });
  };

  // Handle quantity change
  const handleQuantityChange = (itemId, quantity) => {
    setSelectedItems((prevSelectedItems) => {
      if (!prevSelectedItems.includes(itemId)) {
        return [...prevSelectedItems, itemId];
      }
      return prevSelectedItems;
    });

    setQuantities((prevQuantities) => {
      const existingItem = prevQuantities.find((q) => q.id === itemId);
      if (existingItem) {
        return prevQuantities.map((q) => (q.id === itemId ? { ...q, quantity } : q));
      } else {
        return [...prevQuantities, { id: itemId, quantity }];
      }
    });
  };

  // Handle numeric input
  const handleNumericChange = (setter, text) => {
    const numericValue = text.replace(/[^0-9.]/g, '');
    setter(numericValue);
  };

  // Helper function to check if all selected items have valid quantities
  const areAllSelectedItemsValid = () => {
    if (selectedItems.length === 0) return false;
    return selectedItems.every((itemId) => {
      const quantity = quantities.find((q) => q.id === itemId);
      return quantity && parseFloat(quantity.quantity) > 0;
    });
  };

  // Submit items
  const handleItemsSubmit = async () => {
    if (loading) return;

    // Validation: At least one category must be selected
    if (categoryChips.length === 0) {
      toast.error('Please select at least one category.');
      return;
    }

    // Validation: At least one item must be selected
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item.');
      return;
    }

    // Validation: All selected items must have quantity > 0
    const invalidItems = quantities.filter((q) => {
      const isSelected = selectedItems.includes(q.id);
      const hasQuantity = q.quantity && parseFloat(q.quantity) > 0;
      return isSelected && !hasQuantity;
    });

    if (invalidItems.length > 0) {
      toast.error('All selected items must have a quantity greater than 0.');
      return;
    }

    setLoading(true);

    const selectedQuantities = quantities
      .filter((q) => selectedItems.includes(q.id))
      .map((q) => ({
        item_id: q.id,
        quantity: q.quantity
      }));

    const payload = {
      project_id: projectId,
      category_ids: categoryChips.map((chip) => chip.id),
      task_id: selectedTask?.task_id,
      items: selectedQuantities,
      dailyTask_id: selectedTask?.id
    };

    try {
      await addRequirement(payload);
      toast.success('Items requirement added successfully!');
      setRequirementStep('machinery');
      setTrackSkip({ ...trackSkip, items: false });
    } catch (err) {
      console.error('Failed to add items requirement:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error adding items requirement');
    } finally {
      setLoading(false);
    }
  };

  // Submit machinery
  const handleMachinerySubmit = async () => {
    if (loading) return;

    // Validation: At least one machine must be selected
    if (selectedMachines.length === 0) {
      toast.error('Please select at least one machine.');
      return;
    }

    setLoading(true);

    const payload = {
      dailyTask_id: selectedTask?.id,
      task_id: selectedTask?.task_id,
      machines: selectedMachines?.map((machine) => ({
        machine_id: machine.id
      }))
    };

    try {
      await addMachineryRequirement(payload);
      toast.success('Machinery requirement added successfully!');
      setTrackSkip({ ...trackSkip, machinery: false });
      setRequirementStep('labour');
    } catch (err) {
      console.error('Failed to add machinery requirement:', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error adding machinery requirement'
      );
    } finally {
      setLoading(false);
    }
  };

  // Submit labour
  const handleLabourSubmit = async () => {
    if (loading) return;

    // Validation: At least one labour type must have value > 0
    const skilledValue = parseFloat(skilledLabour) || 0;
    const unskilledValue = parseFloat(unskilledLabour) || 0;

    if (skilledValue <= 0 && unskilledValue <= 0) {
      toast.error('Please enter at least one labour type with value greater than 0.');
      return;
    }

    setLoading(true);

    const payload = {
      skilledLabourRequired: skilledLabour ? parseFloat(skilledLabour) : 0,
      unskilledLabourRequired: unskilledLabour ? parseFloat(unskilledLabour) : 0,
      dailyTask_id: selectedTask?.id
    };

    try {
      await updateLiveTask(selectedTask?.task_id, payload);
      toast.success('Labour requirement added successfully!');
      resetAllData();
      onSuccess();
    } catch (err) {
      console.error('Failed to add labour requirement:', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error adding labour requirement'
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle skip
  const handleSkip = async (step) => {
    if (step === 'items') {
      setTrackSkip({ ...trackSkip, items: true });
      setRequirementStep('machinery');
    } else if (step === 'machinery') {
      setTrackSkip({ ...trackSkip, machinery: true });
      setRequirementStep('labour');
    } else if (step === 'labour') {
      // If all steps are skipped, show confirmation modal
      if (trackSkip.items && trackSkip.machinery) {
        setShowDeleteConfirmation(true);
      } else {
        resetAllData();
        onSuccess();
      }
    }
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    try {
      const payload = { id: selectedTask?.id };
      await deleteDailyTask(payload);
      toast.success('Priority completed successfully as all requirements were skipped');
      setShowDeleteConfirmation(false);
      resetAllData();
      onSuccess();
    } catch (error) {
      console.error('Error completing priority:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to complete priority'
      );
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    resetAllData();
    onSuccess();
  };

  // Category chip management
  const addCategoryChip = (selectedList, selectedItem) => {
    setCategoryChips(selectedList);
  };

  const removeCategoryChip = (selectedList, removedItem) => {
    setCategoryChips(selectedList);
  };

  // Machine chip management
  const addMachineChip = (selectedList, selectedItem) => {
    setSelectedMachines(selectedList);
  };

  const removeMachineChip = (selectedList, removedItem) => {
    setSelectedMachines(selectedList);
  };

  return (
    <>
      <Modal
        isDirty
        confirmOnClose
        width={70}
        name={`Add Requirements (${requirementStep === 'items' ? '1' : requirementStep === 'machinery' ? '2' : '3'}/3)`}
        onClose={handleClose}>
        <div className="flex flex-col justify-start items-center z-10 min-h-[25rem]">
          {/* Task Name with Back Button */}
          <div className="w-full mb-6">
            <div className="flex items-center justify-between">
              {(requirementStep === 'machinery' || requirementStep === 'labour') && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  <span className="text-sm font-medium">Back</span>
                </button>
              )}
              {requirementStep === 'items' && <div></div>}
              <h3 className="text-lg font-semibold text-gray-800 text-center flex-1">
                {selectedTask?.liveTask?.name || selectedTask?.name || 'Task Name'}
              </h3>
              <div></div> {/* Spacer for centering */}
            </div>
          </div>

          {/* Items Step */}
          {requirementStep === 'items' && (
            <div className="w-full flex flex-col justify-between min-h-[20rem]">
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-700 flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-600" />
                  Select Categories & Items
                </h4>

                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categories *
                  </label>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Multiselect
                        options={Array.isArray(categories) ? categories : []}
                        selectedValues={categoryChips}
                        onSelect={addCategoryChip}
                        onRemove={removeCategoryChip}
                        displayValue="name"
                        placeholder="Select categories..."
                        showCheckbox
                        style={{
                          optionContainer: {
                            maxHeight: '200px'
                          }
                        }}
                        emptyRecordMsg="No categories available"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={() => setOpenAddMaterial(true)}
                      customStyle={{
                        height: '2.5rem',
                        paddingLeft: '1rem',
                        paddingRight: '1rem',
                        whiteSpace: 'nowrap'
                      }}>
                      Add
                    </Button>
                  </div>
                </div>

                {/* Items List */}
                {items.length === 0 && categoryChips.length > 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <Package className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Inventory Added</h3>
                    <p className="text-sm text-gray-500 max-w-sm">
                      No items found for the selected categories. Please select categories to view
                      available items.
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <label className="text-sm font-medium text-gray-700">
                        Items ({selectedItems.length} selected)
                      </label>
                    </div>

                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {Array.isArray(items) &&
                        items.map((item) => {
                          const matchedItem = quantities.find((q) => q.id === item.id);
                          return (
                            <div
                              key={item.id}
                              className="flex items-center gap-3 p-3 border border-gray-200 rounded-md">
                              <input
                                type="checkbox"
                                checked={selectedItems.includes(item.id)}
                                onChange={() => toggleItemSelection(item.id)}
                                className="w-4 h-4 text-blue-600"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-800">
                                  {item.item?.name || item.name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Unit: {item.unit || 'N/A'}
                                </div>
                              </div>
                              <div className="w-24">
                                <input
                                  type="number"
                                  placeholder="Qty"
                                  value={matchedItem?.quantity?.toString() || ''}
                                  onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                  className={`w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-1 ${
                                    selectedItems.includes(item.id) &&
                                    (!matchedItem?.quantity ||
                                      parseFloat(matchedItem.quantity) <= 0)
                                      ? 'border-red-300 focus:ring-red-500 bg-red-50'
                                      : 'border-gray-300 focus:ring-blue-500'
                                  }`}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center pt-4 border-t">
                <button
                  onClick={() => handleSkip('items')}
                  className="text-blue-600 hover:text-blue-800 text-sm underline">
                  Skip →
                </button>
                <Button
                  onClick={handleItemsSubmit}
                  disabled={loading || categoryChips.length === 0 || !areAllSelectedItemsValid()}>
                  {loading ? 'Adding...' : 'Next'}
                </Button>
              </div>
            </div>
          )}

          {/* Machinery Step */}
          {requirementStep === 'machinery' && (
            <div className="w-full space-y-6">
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-700 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-green-600" />
                  Select Machinery
                </h4>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Machines *</label>
                  <Multiselect
                    options={Array.isArray(machineList) ? machineList : []}
                    selectedValues={selectedMachines}
                    onSelect={addMachineChip}
                    onRemove={removeMachineChip}
                    displayValue="name"
                    placeholder="Select machines..."
                    showCheckbox
                    style={{
                      optionContainer: {
                        maxHeight: '200px'
                      }
                    }}
                    emptyRecordMsg="No machines available"
                  />
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center pt-4 border-t">
                <button
                  onClick={() => handleSkip('machinery')}
                  className="text-blue-600 hover:text-blue-800 text-sm underline">
                  Skip →
                </button>
                <Button
                  onClick={handleMachinerySubmit}
                  disabled={loading || selectedMachines.length === 0}>
                  {loading ? 'Adding...' : 'Next'}
                </Button>
              </div>
            </div>
          )}

          {/* Labour Step */}
          {requirementStep === 'labour' && (
            <div className="w-full space-y-6">
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-700 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  Labour Requirements
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Skilled Labour
                    </label>
                    <input
                      type="number"
                      placeholder="0"
                      value={skilledLabour}
                      onChange={(e) => handleNumericChange(setSkilledLabour, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unskilled Labour
                    </label>
                    <input
                      type="number"
                      placeholder="0"
                      value={unskilledLabour}
                      onChange={(e) => handleNumericChange(setUnskilledLabour, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                    />
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center pt-4 border-t">
                <button
                  onClick={() => handleSkip('labour')}
                  className="text-blue-600 hover:text-blue-800 text-sm underline">
                  Skip →
                </button>
                <Button
                  onClick={handleLabourSubmit}
                  disabled={
                    loading ||
                    ((parseFloat(skilledLabour) || 0) <= 0 &&
                      (parseFloat(unskilledLabour) || 0) <= 0)
                  }>
                  {loading ? 'Adding...' : 'Finish'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {OpenAddMaterial && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={40}
          name="Add Materials"
          onClose={() => setOpenAddMaterial(false)}>
          <AddMaterials
            onSubmit={() => {
              toast.success('Material created successfully');
              refetchCategories();
              if (categoryChips) {
                fetchItemsByCategories();
              }
            }}
            onClose={() => setOpenAddMaterial(false)}
            onSuccess={() => {
              refetchCategories();
              if (categoryChips) {
                fetchItemsByCategories();
              }
            }}
            hideBrand={true}
          />
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <Modal
          isDirty={false}
          confirmOnClose={false}
          width={40}
          name="Confirm Priority Completion"
          onClose={handleCancelDelete}>
          <div className="flex flex-col items-center justify-center py-8 px-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
              Complete Priority?
            </h3>

            <p className="text-sm text-gray-600 text-center mb-6 max-w-sm">
              All requirements have been skipped. This will complete the priority &quot;
              {selectedTask?.liveTask?.name || selectedTask?.name || 'Priority'}&quot; with no
              requirements and cannot be undone.
            </p>

            <div className="flex gap-3 w-full max-w-xs">
              <Button
                onClick={handleCancelDelete}
                customStyle={{
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  flex: 1
                }}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                customStyle={{
                  backgroundColor: '#DC2626',
                  color: 'white',
                  border: 'none',
                  flex: 1
                }}>
                Complete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default AddRequirementAll;
